const SENSITIVE_KEY =
  /(?:^|[._-])(?:api[-_]?key|consumerKey|consumerSecret|clientSecret|privateKey|[a-z]*token|password|secret|valid[-_]?webhook|callbackState|clerk[-_]?ticket|input)$/i;

function scrubSensitiveQuery(value: string): string {
  return value.replace(
    /(^|[?&])([^=&?#\s]+)=([^&#\s]*)/g,
    (match, prefix: string, key: string) => {
      try {
        return SENSITIVE_KEY.test(decodeURIComponent(key))
          ? `${prefix}${key}=[Filtered]`
          : match;
      } catch {
        return match;
      }
    },
  );
}

function scrubSensitiveKeys(
  value: unknown,
  seen: Set<unknown> = new Set(),
): unknown {
  if (Array.isArray(value)) {
    if (
      value.length === 2 &&
      typeof value[0] === 'string' &&
      SENSITIVE_KEY.test(value[0])
    ) {
      return [value[0], '[Filtered]'];
    }
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
    const out = value.map((item) => scrubSensitiveKeys(item, seen));
    seen.delete(value);
    return out;
  }
  if (value !== null && typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return value;
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
    const out: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value)) {
      out[key] = SENSITIVE_KEY.test(key)
        ? '[Filtered]'
        : scrubSensitiveKeys(nested, seen);
    }
    seen.delete(value);
    return out;
  }
  return typeof value === 'string' ? scrubSensitiveQuery(value) : value;
}

export function scrubSentrySensitiveData<E extends object>(event: E): E {
  const sanitized = scrubSensitiveKeys(event) as Record<string, unknown>;
  if (sanitized.request && typeof sanitized.request === 'object') {
    const request = sanitized.request as Record<string, unknown>;
    // Request bodies, headers and query values can contain arbitrary customer data.
    sanitized.request = {
      method: request.method,
      url:
        typeof request.url === 'string'
          ? request.url.split(/[?#]/)[0]
          : undefined,
    };
  }
  if (sanitized.user && typeof sanitized.user === 'object') {
    const { id } = sanitized.user as Record<string, unknown>;
    sanitized.user =
      typeof id === 'string' || typeof id === 'number' ? { id } : {};
  }
  return Object.assign(event, sanitized);
}

type SentryBeforeSendEvent = {
  contexts?: {
    trpc?: Record<string, unknown>;
    [key: string]: unknown;
  };
};

function isIgnorableSentryException(error: unknown): boolean {
  const name = error instanceof Error ? error.name : undefined;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : undefined;
  return (
    name === 'AbortError' ||
    message === 'The user aborted a request' ||
    message === 'Too many requests'
  );
}

export function applySentryBeforeSend<E extends SentryBeforeSendEvent>(
  event: E,
  hint: { originalException?: unknown },
  isDroppableTrpcError: (error: unknown) => boolean,
): E | null {
  if (
    isDroppableTrpcError(hint.originalException) ||
    isIgnorableSentryException(hint.originalException)
  ) {
    return null;
  }
  return scrubSentrySensitiveData(event);
}
